import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaTimes, FaPlus, FaTrash, FaSave, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { apiService } from '../../services/apiService';
import { logger } from '../../utils/logger';
import { useToast } from '../../hooks/useToast';

const OPERATORI = ['eq', 'like', 'in', 'gte', 'lte', 'gt', 'lt'];

/**
 * Sub-editor per la child table SKE_CampiOperatori.
 * Gestisce le righe (COP_Operatore, COP_SuffissoFiltro, COP_Sequenza) per un campo padre.
 *
 * Mostrato quando CAM_Cercabile === 1.
 * Quando CAM_Cercabile === 0 le righe restano in DB ma viene mostrato solo un warning.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - campoId: number (CAM_Id_Campi del padre)
 * - campoNome: string (per il titolo)
 * - cercabile: boolean (se false mostra solo il warning, niente editor)
 * - themeColor: tailwind color
 */
const CampiOperatoriEditor = ({
  isOpen,
  onClose,
  campoId,
  campoNome,
  cercabile = true,
  themeColor = 'purple',
}) => {
  const { showSuccess, showError } = useToast();
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const loadRows = useCallback(async () => {
    if (!campoId || campoId < 0) {
      setRows([]);
      setOriginalRows([]);
      return;
    }
    setIsLoading(true);
    try {
      const params = {
        limit: 9999,
        offset: 0,
        filters: JSON.stringify({ COP_Id_Campi: campoId }),
      };
      const response = await apiService.get('/crud/SKE_CampiOperatori', { params });
      const data = response?.records || response?.data || response || [];
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => (a.COP_Sequenza || 0) - (b.COP_Sequenza || 0))
        : [];
      setRows(sorted);
      setOriginalRows(sorted);
    } catch (err) {
      logger.error('[CampiOperatoriEditor] Errore caricamento operatori:', err);
      // Tabella potrebbe non esistere ancora: tratta come vuoto
      setRows([]);
      setOriginalRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [campoId]);

  useEffect(() => {
    if (isOpen && cercabile) {
      loadRows();
    }
  }, [isOpen, cercabile, loadRows]);

  const nextSequenza = useMemo(() => {
    const max = rows.reduce((m, r) => Math.max(m, Number(r.COP_Sequenza) || 0), 0);
    return max + 10;
  }, [rows]);

  const handleAddRow = () => {
    setRows(prev => [
      ...prev,
      {
        COP_Id_CampiOperatori: -(Date.now() + prev.length),
        COP_Id_Campi: campoId,
        COP_Operatore: 'eq',
        COP_SuffissoFiltro: '',
        COP_Sequenza: nextSequenza,
        _isNew: true,
      },
    ]);
  };

  const handleDeleteRow = (id) => {
    setRows(prev => prev.filter(r => r.COP_Id_CampiOperatori !== id));
  };

  const handleChange = (id, field, value) => {
    setRows(prev => prev.map(r =>
      r.COP_Id_CampiOperatori === id
        ? { ...r, [field]: value, _isDirty: !r._isNew ? true : r._isDirty }
        : r
    ));
  };

  const validate = () => {
    const errs = {};
    const seen = new Set();
    rows.forEach(r => {
      if (!r.COP_Operatore) {
        errs[r.COP_Id_CampiOperatori] = 'Operatore obbligatorio';
        return;
      }
      const key = `${r.COP_Operatore}::${(r.COP_SuffissoFiltro || '').trim()}`;
      if (seen.has(key)) {
        errs[r.COP_Id_CampiOperatori] = `Coppia (${r.COP_Operatore}, ${r.COP_SuffissoFiltro || '∅'}) duplicata`;
      }
      seen.add(key);
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showError('Correggi gli errori prima di salvare');
      return;
    }
    setIsSaving(true);
    try {
      const changes = [];

      // Create / Update
      rows.forEach(r => {
        if (r._isNew) {
          const { COP_Id_CampiOperatori, _isNew, _isDirty, ...data } = r;
          changes.push({ operation: 'create', data });
        } else {
          const orig = originalRows.find(o => o.COP_Id_CampiOperatori === r.COP_Id_CampiOperatori);
          if (orig && (
            orig.COP_Operatore !== r.COP_Operatore ||
            (orig.COP_SuffissoFiltro || '') !== (r.COP_SuffissoFiltro || '') ||
            Number(orig.COP_Sequenza) !== Number(r.COP_Sequenza)
          )) {
            changes.push({
              operation: 'update',
              data: {
                COP_Id_CampiOperatori: r.COP_Id_CampiOperatori,
                COP_Operatore: r.COP_Operatore,
                COP_SuffissoFiltro: r.COP_SuffissoFiltro || null,
                COP_Sequenza: Number(r.COP_Sequenza) || 0,
              },
            });
          }
        }
      });

      // Delete
      originalRows.forEach(o => {
        if (!rows.find(r => r.COP_Id_CampiOperatori === o.COP_Id_CampiOperatori)) {
          changes.push({
            operation: 'delete',
            data: { COP_Id_CampiOperatori: o.COP_Id_CampiOperatori },
          });
        }
      });

      if (changes.length === 0) {
        showSuccess('Nessuna modifica da salvare');
        onClose?.();
        return;
      }

      const response = await apiService.post('/crud/SKE_CampiOperatori', { changes });
      if (response && response.results && response.results.success) {
        showSuccess('Operatori salvati con successo');
        await loadRows();
        onClose?.();
      } else {
        showError('Errore durante il salvataggio degli operatori');
      }
    } catch (err) {
      logger.error('[CampiOperatoriEditor] Errore salvataggio:', err);
      showError(`Errore: ${err.message || 'Sconosciuto'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className={`flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-${themeColor}-50 dark:bg-${themeColor}-900/30 rounded-t-lg`}>
          <h3 className={`text-lg font-bold text-${themeColor}-800 dark:text-${themeColor}-100`}>
            Operatori di ricerca {campoNome ? `– ${campoNome}` : ''}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-300">
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!cercabile ? (
            <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded">
              <FaExclamationTriangle className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                Il campo non è cercabile: gli operatori esistenti non saranno emessi nei template.
                Le righe presenti in DB restano ma non sono modificabili da qui.
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex justify-center items-center py-8">
              <FaSpinner className="animate-spin text-2xl text-gray-400" />
            </div>
          ) : (
            <>
              <div className="mb-3 flex justify-between items-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Definisci gli operatori utilizzabili nei filtri di template per questo campo.
                </p>
                <button
                  type="button"
                  onClick={handleAddRow}
                  className={`px-3 py-1.5 text-sm bg-${themeColor}-600 text-white rounded-md hover:bg-${themeColor}-700 flex items-center gap-1.5`}
                >
                  <FaPlus className="h-3 w-3" /> Aggiungi
                </button>
              </div>

              {rows.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  Nessun operatore definito.
                </div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700 text-left">
                      <th className="px-3 py-2 font-medium">Operatore *</th>
                      <th className="px-3 py-2 font-medium">Suffisso filtro</th>
                      <th className="px-3 py-2 font-medium w-24">Sequenza</th>
                      <th className="px-3 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <React.Fragment key={r.COP_Id_CampiOperatori}>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <td className="px-3 py-2">
                            <select
                              value={r.COP_Operatore || 'eq'}
                              onChange={e => handleChange(r.COP_Id_CampiOperatori, 'COP_Operatore', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                            >
                              {OPERATORI.map(op => (
                                <option key={op} value={op}>{op}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={r.COP_SuffissoFiltro || ''}
                              onChange={e => handleChange(r.COP_Id_CampiOperatori, 'COP_SuffissoFiltro', e.target.value)}
                              placeholder="_da, _a, _dopo, _prima o vuoto"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={r.COP_Sequenza ?? ''}
                              onChange={e => handleChange(r.COP_Id_CampiOperatori, 'COP_Sequenza', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleDeleteRow(r.COP_Id_CampiOperatori)}
                              className="text-red-600 hover:text-red-800"
                              title="Elimina riga"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                        {errors[r.COP_Id_CampiOperatori] && (
                          <tr>
                            <td colSpan={4} className="px-3 pb-2 text-xs text-red-500">
                              {errors[r.COP_Id_CampiOperatori]}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <FaTimes className="inline mr-1.5 h-4 w-4" /> Chiudi
          </button>
          {cercabile && (
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className={`px-4 py-2 text-sm bg-${themeColor}-600 text-white rounded-md hover:bg-${themeColor}-700 disabled:opacity-50 flex items-center`}
            >
              {isSaving ? <FaSpinner className="animate-spin mr-1.5 h-4 w-4" /> : <FaSave className="mr-1.5 h-4 w-4" />}
              Salva
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampiOperatoriEditor;
